import { authApi } from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';
import { logger } from 'utils/logger';
import { reportNativeError } from 'utils/native/nativeTelemetry';
import {
  buildPublicKeyOptions,
  getPasskeyRpId,
  isPasskeyAlreadyEnrolledError,
  isPasskeyCanceledError,
  serializeCredentialResponse,
  WEBAUTHN_KIND,
} from 'utils/webAuthn';

const ENROLL_PASSKEY_REQUEST = 'ENROLL_PASSKEY_REQUEST';
const ENROLL_PASSKEY_SUCCESS = 'ENROLL_PASSKEY_SUCCESS';
const ENROLL_PASSKEY_FAILURE = 'ENROLL_PASSKEY_FAILURE';

const GET_PASSKEYS_REQUEST = 'GET_PASSKEYS_REQUEST';
const GET_PASSKEYS_SUCCESS = 'GET_PASSKEYS_SUCCESS';

const DELETE_PASSKEY_REQUEST = 'DELETE_PASSKEY_REQUEST';
const DELETE_PASSKEY_SUCCESS = 'DELETE_PASSKEY_SUCCESS';

/**
 * DEV-3881 post-auth passkey enrollment (the `create()` counterpart of `submitPasskeySignInAction`).
 * Runs the authenticated WebAuthn registration ceremony: fetch spec-compliant creation options, hand them to
 * `navigator.credentials.create()`, then post the attestation. The challenge / user.id / excludeCredentials ids
 * arrive base64url (Fido2NetLib's `ToJson()` contract — DEV-3881 backend fix) and must be decoded to ArrayBuffers
 * for the browser API, and the attestation byte[] members re-encoded base64url for the backend.
 */
export const enrollPasskeyAction =
  (nickname = null) =>
  async dispatch => {
    dispatch({ type: ENROLL_PASSKEY_REQUEST });

    try {
      const { data: options } = await authApi.getPasskeyRegistrationOptions();

      const credential = await navigator.credentials.create({
        publicKey: buildPublicKeyOptions(options, WEBAUTHN_KIND.CREATE),
      });

      const attestationResponseJson = serializeCredentialResponse(credential, WEBAUTHN_KIND.CREATE);

      const { data } = await authApi.registerPasskey({
        rpId: getPasskeyRpId(),
        attestationResponseJson,
        nickname,
      });

      dispatch({ type: ENROLL_PASSKEY_SUCCESS });

      return data;
    } catch (error) {
      // Report only GENUINE failures to App Insights. "Already enrolled on this device"
      // (InvalidStateError / iOS matchedExcludedCredential 1006) and user-cancel are EXPECTED outcomes,
      // not exceptions — reporting them would pollute the error stream and inflate alerting. Everything
      // else stays reported: the native shell has no console, so this is the only channel that exposes
      // why an iOS/Android passkey failure happened on-device (DEV-4084).
      if (!isPasskeyAlreadyEnrolledError(error) && !isPasskeyCanceledError(error)) {
        logger.error(
          `[passkey-enroll] failed: ${error?.name || 'Error'}: ${
            error?.message || String(error)
          } ` + `rpId=${getPasskeyRpId()}`,
        );
        reportNativeError(error, { context: 'passkey-enroll', rpId: getPasskeyRpId() });
      }

      dispatch({ type: ENROLL_PASSKEY_FAILURE, payload: error });

      throw error;
    }
  };

/**
 * Fetches the authenticated user's enrolled passkeys from GET /api/passkey.
 * Resolves to the passkey array so callers can update local state.
 * Uses catchAsyncActionError so network failures surface as the global error toast.
 */
export const getPasskeysAction = catchAsyncActionError(() => async dispatch => {
  dispatch({ type: GET_PASSKEYS_REQUEST });

  const { data } = await authApi.getPasskeys();

  dispatch({ type: GET_PASSKEYS_SUCCESS, payload: data });

  return data;
});

/**
 * Deletes a single passkey by id (DELETE /api/passkey/{id}).
 * Uses catchAsyncActionError so network failures surface as the global error toast.
 * The caller is responsible for refreshing the list after deletion.
 */
export const deletePasskeyAction = catchAsyncActionError(({ id }) => async dispatch => {
  dispatch({ type: DELETE_PASSKEY_REQUEST });

  await authApi.deletePasskey(id);

  dispatch({ type: DELETE_PASSKEY_SUCCESS, payload: id });
});
